import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@tw/tw-component-library';
import styled from 'styled-components';

const FileDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.colors.backgroundHover};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  margin-top: 0.25rem;
`;

const FileLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const FileIcon = styled.span`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FileList = styled.div`
  margin-top: 0.25rem;
  padding-left: 1.5rem;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.125rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RemoveButton = styled.button`
  margin-left: auto;
  padding: 0.125rem 0.5rem;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
`;

export interface FileDisplayFileObject {
  fileName?: string;
  name?: string;
  url?: string;
  blobUrl?: string;
  blobURL?: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

interface FileDisplayProps {
  fileName?: string | File;
  workflowId?: string;
  showViewFilesLink?: boolean;
  workflowType?: string;
  /** When true, show controls to remove individual files or the full set */
  allowRemove?: boolean;
  /**
   * Called with the updated file value (or '' when cleared) and any removed
   * file metadata so callers can queue blob URLs for deletion on submit.
   */
  onFilesChange?: (
    value: string,
    meta?: { removed: FileDisplayFileObject[] }
  ) => void;
}

interface ParsedFileData {
  isMultiple: boolean;
  fileNames: string[];
  originalData?: unknown;
}

type FileObject = FileDisplayFileObject;

const serializeRemainingFiles = (remaining: FileObject[]): string => {
  if (remaining.length === 0) {
    return '';
  }
  if (remaining.length === 1) {
    return JSON.stringify(remaining[0]);
  }
  return JSON.stringify(remaining);
};

/**
 * Reusable FileDisplay component for showing uploaded files in forms
 *
 * @param fileName - The file name or File object to display
 * @param workflowId - Optional workflow ID for the "View Files" link
 * @param showViewFilesLink - Whether to show the "View Files" link (default: true)
 * @param workflowType - Optional workflow type for the "View Files" link
 * @param allowRemove - Whether to show remove controls
 * @param onFilesChange - Callback when files are removed
 */
export const FileDisplay: React.FC<FileDisplayProps> = ({
  fileName,
  workflowId,
  workflowType,
  showViewFilesLink = true,
  allowRemove = false,
  onFilesChange,
}) => {
  const { theme } = useTheme();

  const parseFileData = (
    fileData: string | File | undefined
  ): ParsedFileData | null => {
    if (!fileData) return null;

    // If it's a File object, return single file
    if (fileData instanceof File) {
      return {
        isMultiple: false,
        fileNames: [fileData.name],
        originalData: fileData,
      };
    }

    // If it's a string, check if it needs parsing
    if (typeof fileData === 'string') {
      const trimmedData = fileData.trim();

      // Handle JSON parsing for both single objects and arrays
      if (trimmedData.startsWith('[') && trimmedData.endsWith(']')) {
        // Array of files
        try {
          const parsed = JSON.parse(fileData);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const fileNames = parsed
              .map(
                (file: FileObject) =>
                  file?.fileName || file?.name || 'Unknown file'
              )
              .filter(Boolean);

            return {
              isMultiple: true,
              fileNames,
              originalData: parsed,
            };
          }
        } catch {
          // Fall back to treating as plain string
        }
      } else if (trimmedData.startsWith('{') && trimmedData.endsWith('}')) {
        // Single file object
        try {
          const parsed = JSON.parse(fileData) as FileObject;
          if (
            parsed &&
            typeof parsed === 'object' &&
            (parsed.fileName || parsed.name)
          ) {
            return {
              isMultiple: false,
              fileNames: [parsed.fileName || parsed.name || 'Unknown file'],
              originalData: parsed,
            };
          }
        } catch {
          // Fall back to treating as plain string
        }
      }

      // Treat as plain string (single file name)
      return {
        isMultiple: false,
        fileNames: [fileData],
        originalData: fileData,
      };
    }

    // Fallback for any other type
    return {
      isMultiple: false,
      fileNames: [String(fileData)],
      originalData: fileData,
    };
  };

  const parsedFileData = parseFileData(fileName);

  if (!parsedFileData) {
    return (
      <span style={{ color: theme.colors.text.secondary, fontStyle: 'italic' }}>
        No file uploaded
      </span>
    );
  }

  const canRemove = allowRemove && typeof onFilesChange === 'function';

  const toRemovableFileObjects = (data: unknown): FileDisplayFileObject[] => {
    if (Array.isArray(data)) {
      return data.filter(
        (item): item is FileDisplayFileObject =>
          Boolean(item) && typeof item === 'object' && !(item instanceof File)
      );
    }
    if (data && typeof data === 'object' && !(data instanceof File)) {
      return [data as FileDisplayFileObject];
    }
    return [];
  };

  const handleRemoveAll = (): void => {
    if (!canRemove || !parsedFileData) return;
    onFilesChange('', {
      removed: toRemovableFileObjects(parsedFileData.originalData),
    });
  };

  const handleRemoveAtIndex = (index: number): void => {
    if (!canRemove || !parsedFileData) return;

    const original = parsedFileData.originalData;

    if (Array.isArray(original)) {
      const removed = toRemovableFileObjects(original[index]);
      const remaining = (original as FileObject[]).filter((_, i) => i !== index);
      onFilesChange(serializeRemainingFiles(remaining), { removed });
      return;
    }

    // Single file / plain string / File object — clear the field
    onFilesChange('', {
      removed: toRemovableFileObjects(original),
    });
  };

  const renderFileDisplay = (data: ParsedFileData) => {
    if (data.isMultiple && data.fileNames.length > 1) {
      // Show summary for multiple files
      const shouldShowList = data.fileNames.length <= 3;
      return (
        <div style={{ width: '100%' }}>
          <ActionsRow>
            <FileIcon>📄</FileIcon>
            <span>{data.fileNames.length} files uploaded</span>
            {showViewFilesLink && workflowId && (
              <FileLink to={`/files/${workflowType}/${workflowId}`}>
                View Files
              </FileLink>
            )}
            {canRemove && (
              <RemoveButton
                type="button"
                onClick={handleRemoveAll}
                aria-label="Remove all attached files"
              >
                Remove all
              </RemoveButton>
            )}
          </ActionsRow>
          {shouldShowList && (
            <FileList>
              {data.fileNames.map((name, index) => (
                <FileItem key={index}>
                  <span
                    style={{
                      fontSize: '0.8rem',
                      color: theme.colors.text.secondary,
                    }}
                  >
                    •
                  </span>
                  <span>{name}</span>
                  {canRemove && (
                    <RemoveButton
                      type="button"
                      onClick={() => handleRemoveAtIndex(index)}
                      aria-label={`Remove ${name}`}
                    >
                      Remove
                    </RemoveButton>
                  )}
                </FileItem>
              ))}
            </FileList>
          )}
        </div>
      );
    }

    // Single file display
    return (
      <ActionsRow>
        <FileIcon>📄</FileIcon>
        <span>{data.fileNames[0] || 'Unknown file'}</span>
        {showViewFilesLink && workflowId && (
          <FileLink to={`/files/${workflowType}/${workflowId}`}>
            View Files
          </FileLink>
        )}
        {canRemove && (
          <RemoveButton
            type="button"
            onClick={handleRemoveAll}
            aria-label="Remove attached file"
          >
            Remove
          </RemoveButton>
        )}
      </ActionsRow>
    );
  };

  return (
    <FileDisplayContainer>
      {renderFileDisplay(parsedFileData)}
    </FileDisplayContainer>
  );
};
